import type {
  AutomationOwnConnection,
  AutomationStepConnection,
} from '../services/automation-config-api';

/** A step whose credential the person taking over may not use. */
export interface TakeoverOpenStep {
  stepId: string;
  connectionId: string;
  label: string | null;
  serviceDefinitionId: string | null;
}

export interface TakeoverPlan {
  /** Steps that keep their connection: the person may use its credential. */
  kept: AutomationStepConnection[];
  /** Steps that need a connection of the person or a shared credential. */
  open: TakeoverOpenStep[];
  /** The chosen connections, one per open step that has one. */
  choices: Array<{ stepId: string; connectionId: string }>;
  /** Every open step has a chosen connection, so taking over can start. */
  complete: boolean;
}

/**
 * What taking over needs: a step stays open while one of its connections is
 * unusable for the person and no connection of their own was chosen for it.
 * A step with several connections gets one chosen connection for all of
 * them, as the server sets every connection of the step to it.
 */
export function takeoverPlan(
  connections: readonly AutomationStepConnection[],
  chosen: ReadonlyMap<string, string>
): TakeoverPlan {
  const open = new Map<string, TakeoverOpenStep>();
  for (const entry of connections) {
    if (entry.usable || open.has(entry.stepId)) continue;
    open.set(entry.stepId, {
      stepId: entry.stepId,
      connectionId: entry.connectionId,
      label: entry.label,
      serviceDefinitionId: entry.serviceDefinitionId,
    });
  }
  const choices = [...open.keys()].flatMap(stepId => {
    const connectionId = chosen.get(stepId);
    return connectionId ? [{ stepId, connectionId }] : [];
  });
  return {
    kept: connections.filter(entry => !open.has(entry.stepId)),
    open: [...open.values()],
    choices,
    complete: choices.length === open.size,
  };
}

/** The person's connections that can replace one of `serviceDefinitionId`. */
export function ownConnectionsOf(
  own: readonly AutomationOwnConnection[],
  serviceDefinitionId: string | null
): AutomationOwnConnection[] {
  return serviceDefinitionId
    ? own.filter(entry => entry.serviceDefinitionId === serviceDefinitionId)
    : [];
}
